import { jevBudgetEstimateWords, jevBudgetGateOf, type JevBudgetGate } from '@nest-recruit/shared';
import { useJevBudget } from './jev-budget-api';

// ADR 0070 stage 2: what a button that spends Jev money says about the job's budget. The server refuses a spend that would not fit too
// (hiding is not enforcement); this is the words, so the person sees the cost, what is left, and why a button is off, before pressing.

export interface JevBudgetButton {
  readonly gate: JevBudgetGate;
  /** " · about 8¢ · $1.76 left" for the end of a label; empty when the job has no budget. */
  readonly suffix: string;
  /** " · $1.76 left", for a label that already says its cost; empty when the job has no budget. */
  readonly leftSuffix: string;
  /** "needs about 8¢, 0¢ left. Only the HR Lead can raise this budget. Ask them to raise it.", when blocked; else null. */
  readonly blockedNote: string | null;
}

/** The job's standing as the buttons need it: the budget panel's figures, or undefined while they load (then nothing extra shows). */
export interface JevBudgetStandingView {
  readonly budgetMicroUsd: number | null;
  readonly spentMicroUsd: number;
  readonly canRaise: boolean;
}

/** The smallest cost worth a word: one millionth of a dollar, which reads "under 1¢". */
const SMALLEST_COST_MICRO_USD = 1;

/**
 * The words for one button, from the job's standing and what pressing it is expected to cost (0 = priced by use: refused only when
 * nothing is left, and no cost shown). An action that HAS a price (rank, save and rank, read) passes `isPriced`: when its estimate comes
 * to nothing (no readable CVs yet) it still says "under 1¢" rather than leaving the cost out.
 */
export function jevBudgetButtonOf(standing: JevBudgetStandingView | null | undefined, estimateMicroUsd: number, options: { readonly isPriced?: boolean } = {}): JevBudgetButton {
  if (options.isPriced && estimateMicroUsd <= 0) return jevBudgetButtonOf(standing, SMALLEST_COST_MICRO_USD);
  const gate = jevBudgetGateOf({ budgetMicroUsd: standing?.budgetMicroUsd ?? null, spentMicroUsd: standing?.spentMicroUsd ?? 0, estimateMicroUsd, canRaise: standing?.canRaise ?? false });
  const suffix = gate.hasBudget && gate.leftWords ? ` · ${estimateMicroUsd > 0 ? `${jevBudgetEstimateWords(estimateMicroUsd)} · ` : ''}${gate.leftWords}` : '';
  const blockedNote = gate.isBlocked && gate.blockedWords ? `${gate.blockedWords[0]?.toUpperCase()}${gate.blockedWords.slice(1)}. ${gate.nextStep ?? ''}`.trim() : null;
  const leftSuffix = gate.hasBudget && gate.leftWords ? ` · ${gate.leftWords}` : '';
  return { gate, suffix, leftSuffix, blockedNote };
}

export function useJevBudgetButton(jobId: string, estimateMicroUsd: number, options: { readonly isPriced?: boolean } = {}): JevBudgetButton {
  return jevBudgetButtonOf(useJevBudget(jobId).data, estimateMicroUsd, options);
}
